import { create } from 'zustand';

/** Gumball display mode (3D Space-cycle): translate / rotate / scale. */
export type GumballMode = 'translate' | 'rotate' | 'scale';

/**
 * Raw manipulator handle registry (module-level, non-reactive). Viewport
 * pick contracts (the sketch tool bus, event planes) read it to stand down
 * while the gizmo owns the pointer: three's TransformControls sets `axis`
 * on handle hover and keeps `dragging` true for the whole gesture. R3F's
 * event dispatch runs BEFORE the gizmo's own pointerdown listener (R3F
 * connects first), so at press time `axis` is the only reliable signal —
 * the mouse hover always set it on the way in.
 */
interface GumballControlsLike {
    axis: string | null;
    dragging: boolean;
}

let gumballControls: GumballControlsLike | null = null;

export function registerGumballControls(controls: GumballControlsLike | null): void {
    gumballControls = controls;
}

/** True when the pointer is on a gizmo handle (hover, press or drag). */
export function gumballOwnsPointer(): boolean {
    return !!gumballControls && (gumballControls.dragging || gumballControls.axis !== null);
}

interface GumballState {
    enabled: boolean;
    mode: GumballMode;
    setEnabled: (on: boolean) => void;
    setMode: (mode: GumballMode) => void;
    cycleMode: () => void;
}

export const useGumballStore = create<GumballState>((set) => ({
    enabled: true,
    mode: 'translate',
    setEnabled: (enabled) => set({ enabled }),
    setMode: (mode) => set({ mode }),
    cycleMode: () => set((s) => ({
        mode: s.mode === 'translate' ? 'rotate' : s.mode === 'rotate' ? 'scale' : 'translate',
    })),
}));
